import React, { useEffect, useRef, useState } from 'react';
import { useCart } from '../context/CartContext';
import { useLang } from '../context/LangContext';
import { fixImageUrl, PLACEHOLDER_IMG, unitConfigFor, priceInfo, stockInfo } from '../lib/format';
import Button from '../lib/ui/Button';
import { Badge } from '../lib/ui/Feedback';

export default function ProductDetailContent({ product }) {
  const { addItem } = useCart();
  const { lang } = useLang();
  const te = lang === 'te';
  const [idx, setIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const touchX = useRef(null);

  useEffect(() => {
    if (!product) return;
    setIdx(0);
    setAdded(false);
    const cfg = unitConfigFor(product);
    setQty(cfg.min);
  }, [product?.id]);

  if (!product) return null;

  const cfg = unitConfigFor(product);
  const rawImages = (Array.isArray(product.images) && product.images.length) ? product.images : (product.image_url ? [product.image_url] : []);
  const mapped = rawImages.map(fixImageUrl).filter(Boolean);
  const images = mapped.length ? mapped : [PLACEHOLDER_IMG];
  const { sell, mrp, off, saveAmt } = priceInfo(product);
  const { inStock } = stockInfo(product);

  const nameEn = product.name_en || '';
  const nameTe = product.name_te || '';
  const specs = [
    product.brand ? { label: te ? 'బ్రాండ్' : 'Brand', value: product.brand } : null,
    product.weight ? { label: te ? 'బరువు' : 'Weight', value: product.weight } : null,
    product.origin ? { label: te ? 'ఉత్పత్తి స్థలం' : 'Origin', value: product.origin } : null,
    product.shelf_life ? { label: te ? 'షెల్ఫ్ లైఫ్' : 'Shelf Life', value: product.shelf_life } : null,
  ].filter(Boolean);

  const dec = () => setQty((q) => Math.max(cfg.min, Math.round((q - cfg.step) * 1000) / 1000));
  const inc = () => setQty((q) => Math.round((q + cfg.step) * 1000) / 1000);

  const handleAdd = () => {
    addItem(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) setIdx((i) => Math.min(images.length - 1, i + 1));
    else setIdx((i) => Math.max(0, i - 1));
  };

  return (
    <div>
      {zoomOpen ? (
        <div className="fixed inset-0 z-[400] bg-ink/90 flex items-center justify-center p-6" onClick={() => setZoomOpen(false)}>
          <img src={images[idx]} alt={nameEn} className="max-w-[92vw] max-h-[85vh] object-contain rounded-2xl" />
        </div>
      ) : null}

      <div className="relative" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <button type="button" onClick={() => setZoomOpen(true)} className="block w-full aspect-square bg-brand-light/40">
          <img
            src={images[idx]}
            alt={nameEn}
            className="w-full h-full object-contain"
            onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
          />
        </button>
        {images.length > 1 ? (
          <>
            <button type="button" disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))} className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 shadow-soft font-black disabled:opacity-30">‹</button>
            <button type="button" disabled={idx === images.length - 1} onClick={() => setIdx((i) => Math.min(images.length - 1, i + 1))} className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 shadow-soft font-black disabled:opacity-30">›</button>
          </>
        ) : null}
      </div>

      {images.length > 1 ? (
        <div className="flex gap-2 px-5 pt-3 overflow-x-auto no-scrollbar">
          {images.map((img, i) => (
            <button key={i} type="button" onClick={() => setIdx(i)} className={`shrink-0 h-14 w-14 rounded-xl overflow-hidden border-2 ${i === idx ? 'border-brand' : 'border-transparent'}`}>
              <img src={img} alt={`${nameEn} ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      ) : null}

      <div className="px-5 pt-4 pb-28">
        <div className="text-xl font-black text-ink">{nameEn}</div>
        {nameTe ? <div className="text-ink-faint mt-0.5" style={{ fontFamily: "'Tiro Telugu', serif" }}>{nameTe}</div> : null}

        <div className="flex items-baseline gap-2 flex-wrap mt-3">
          <span className="text-2xl font-black text-ink">₹{sell}<span className="text-sm font-semibold text-ink-faint">/{cfg.label}</span></span>
          {mrp > sell ? <span className="text-ink-faint line-through">₹{mrp}</span> : null}
          {saveAmt > 0 ? <Badge tone="danger">₹{saveAmt} OFF{off > 0 ? ` · ${off}%` : ''}</Badge> : null}
        </div>

        <div className="flex flex-wrap gap-1.5 mt-3">
          <Badge tone={inStock ? 'brand' : 'danger'}>{inStock ? (te ? 'స్టాక్‌లో ఉంది' : 'In Stock') : (te ? 'స్టాక్ లేదు' : 'Out of Stock')}</Badge>
          <Badge tone="neutral">{te ? 'కనిష్ఠం' : 'Min'}: {cfg.min}{cfg.label}</Badge>
          <Badge tone="neutral">{te ? 'స్టెప్' : 'Step'}: {cfg.step}{cfg.label}</Badge>
          {product.effective_return_enabled ? <Badge tone="info">{te ? 'రిటర్న్ అందుబాటులో' : 'Return eligible'}</Badge> : null}
        </div>

        {(product.description_en || product.description_te) ? (
          <>
            <div className="text-xs font-extrabold text-ink-faint uppercase mt-5 mb-1.5">{te ? 'వివరణ' : 'Description'}</div>
            <p className="text-sm text-ink-soft leading-relaxed">
              {te ? (product.description_te || product.description_en) : (product.description_en || product.description_te)}
            </p>
          </>
        ) : null}

        {specs.length > 0 ? (
          <>
            <div className="text-xs font-extrabold text-ink-faint uppercase mt-5 mb-1.5">{te ? 'స్పెక్స్' : 'Specs'}</div>
            <div className="grid grid-cols-2 gap-2">
              {specs.map((s) => (
                <div key={s.label} className="bg-brand-light/40 rounded-xl px-3 py-2">
                  <div className="text-[11px] text-ink-faint font-bold">{s.label}</div>
                  <div className="text-sm font-extrabold text-ink">{s.value}</div>
                </div>
              ))}
            </div>
          </>
        ) : null}

        <div className="flex items-center justify-between mt-6">
          <span className="text-sm font-extrabold text-ink">{te ? 'పరిమాణం' : 'Quantity'}</span>
          <div className="flex items-center gap-3 bg-brand-light/60 rounded-full px-2 py-1.5">
            <button type="button" disabled={!inStock} onClick={dec} className="h-8 w-8 rounded-full bg-white shadow-soft font-black text-brand-dark disabled:opacity-40">−</button>
            <span className="min-w-[64px] text-center font-extrabold text-ink">{qty}{cfg.label}</span>
            <button type="button" disabled={!inStock} onClick={inc} className="h-8 w-8 rounded-full bg-white shadow-soft font-black text-brand-dark disabled:opacity-40">+</button>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-black/5 px-5 py-3.5 flex items-center gap-4">
        <div>
          <div className="text-[11px] text-ink-faint font-bold">{added ? (te ? 'మొత్తం' : 'Total') : (te ? 'ధర' : 'Price')}</div>
          <div className="font-black text-lg text-ink">₹{added ? (sell * qty).toFixed(2) : sell.toFixed(2)}{added ? '' : ` / ${cfg.label}`}</div>
        </div>
        <Button variant={added ? 'primary' : 'accent'} fullWidth disabled={!inStock} onClick={handleAdd} className={added ? '!bg-emerald-600' : ''}>
          {inStock ? (added ? `✓ ${te ? 'జోడించబడింది!' : 'Added!'}` : `+ ${te ? 'కార్ట్‌కు జోడించు' : 'Add to Cart'}`) : (te ? 'స్టాక్ అందుబాటులో లేదు' : 'Currently unavailable')}
        </Button>
      </div>
    </div>
  );
}
